import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const MaterialQualityCheckPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    raw_materials: false,
    supplies: false
  });
  
  // Material Quality Control Check sections
  const qcSections = [
    { 
      key: 'raw_materials', 
      label: 'Chemical Quality Check Page', 
      permissionPrefix: 'inventory.raw_materials.qc_tests',
      icon: <ScienceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
    },
    { 
      key: 'supplies', 
      label: 'Supply Quality Check Page', 
      permissionPrefix: 'inventory.supplies.qc_tests',
      icon: <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
    }
  ];

  const renderQCSection = (section) => {
    const isExpanded = expandedSections[section.key] !== false;
    // Find permissions for this QC section
    const sectionPermissions = permissions.filter(p => 
      p.name && p.name.includes(section.permissionPrefix)
    );

    if (sectionPermissions.length === 0) return null;

    // Group permissions by action type
    const viewPermissions = sectionPermissions.filter(p => p.name.includes('.view'));
    const editPermissions = sectionPermissions.filter(p => p.name.includes('.edit'));
    const deletePermissions = sectionPermissions.filter(p => p.name.includes('.delete'));
    const approvePermissions = sectionPermissions.filter(p => p.name.includes('.approve'));

    const viewPerm = viewPermissions[0];
    const editPerm = editPermissions[0];
    const deletePerm = deletePermissions[0];
    const approvePerm = approvePermissions[0];

    return (
      <Box 
        key={section.key}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Section Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [section.key]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {section.icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {section.label}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Tests Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View Tests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view quality check tests
                  </Typography>
                </Box>
                <Switch
                  checked={viewPermissions.some(p => roleForm.permissions[p.id]?.read) || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    viewPermissions.forEach(permission => {
                      if (!newPermissions[permission.id]) {
                        newPermissions[permission.id] = {};
                      }
                      newPermissions[permission.id].read = e.target.checked;
                    });
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Tests Permission */}
            {editPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit Tests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit quality check tests
                  </Typography>
                </Box>
                <Switch
                  checked={editPermissions.some(p => roleForm.permissions[p.id]?.write) || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    editPermissions.forEach(permission => {
                      if (!newPermissions[permission.id]) {
                        newPermissions[permission.id] = {};
                      }
                      newPermissions[permission.id].write = e.target.checked;
                    });
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Tests Permission */}
            {deletePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: approvePerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete Tests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete quality check tests
                  </Typography>
                </Box>
                <Switch
                  checked={deletePermissions.some(p => roleForm.permissions[p.id]?.delete) || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    deletePermissions.forEach(permission => {
                      if (!newPermissions[permission.id]) {
                        newPermissions[permission.id] = {};
                      }
                      newPermissions[permission.id].delete = e.target.checked;
                    });
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve Tests Permission */}
            {approvePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve Tests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve quality check tests
                  </Typography>
                </Box>
                <Switch
                  checked={approvePermissions.some(p => roleForm.permissions[p.id]?.write) || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    approvePermissions.forEach(permission => {
                      if (!newPermissions[permission.id]) {
                        newPermissions[permission.id] = {};
                      }
                      newPermissions[permission.id].write = e.target.checked;
                    });
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {qcSections.map(section => renderQCSection(section))}
    </Box>
  );
};

export default MaterialQualityCheckPermissions;
